import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useNewsFeed } from '@/hooks/useNewsFeed';
import { RefreshCw, ExternalLink, Bookmark, X, TrendingUp } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

export function NewsFeed() {
  const { articles, isLoading, markAsRead, saveArticle, dismissArticle, refreshFeed, unreadCount } = useNewsFeed();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshFeed();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleArticleClick = (article: typeof articles[0]) => {
    if (!article.is_read) {
      markAsRead(article.id);
    }
    window.open(article.url, '_blank');
  };

  const getRelevanceBadgeVariant = (score: number | null) => {
    if (!score) return 'secondary';
    if (score >= 0.8) return 'default';
    if (score >= 0.6) return 'secondary';
    return 'outline';
  };

  const getSentimentColor = (sentiment: string | null) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600';
      case 'negative': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              News Feed
              {unreadCount > 0 && (
                <Badge variant="default" className="ml-2">
                  {unreadCount} new
                </Badge>
              )}
            </CardTitle>
            <CardDescription>Stay updated on industry news</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">No news articles yet</p>
            <Button onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Fetch News
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article) => (
              <div
                key={article.id}
                className={`group border rounded-lg p-4 transition-all hover:shadow-md ${
                  article.is_read ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {article.relevance_score && (
                        <Badge variant={getRelevanceBadgeVariant(article.relevance_score)}>
                          {Math.round(article.relevance_score * 100)}%
                        </Badge>
                      )}
                      {article.sentiment && (
                        <span className={`text-sm ${getSentimentColor(article.sentiment)}`}>
                          {article.sentiment === 'positive' ? '🟢' : article.sentiment === 'negative' ? '🔴' : '🟡'}
                        </span>
                      )}
                    </div>
                    <h3
                      className="font-semibold cursor-pointer hover:text-primary transition-colors"
                      onClick={() => handleArticleClick(article)}
                    >
                      {article.title}
                    </h3>
                  </div>
                </div>

                {article.summary && (
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {article.summary}
                  </p>
                )}

                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <span>{article.source}</span>
                  <span>•</span>
                  <span>{formatDistanceToNow(new Date(article.published_at), { addSuffix: true })}</span>
                </div>

                {article.categories && article.categories.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {article.categories.map((category) => (
                      <Badge key={category} variant="outline" className="text-xs">
                        {category.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                )}

                {article.related_companies && article.related_companies.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {article.related_companies.map((company) => (
                      <Badge key={company} variant="secondary" className="text-xs">
                        🏢 {company}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleArticleClick(article)}
                    className="h-7 text-xs"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Read
                  </Button>
                  {!article.is_saved && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => saveArticle(article.id)}
                      className="h-7 text-xs"
                    >
                      <Bookmark className="h-3 w-3 mr-1" />
                      Save
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissArticle(article.id)}
                    className="h-7 text-xs"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
